// Slice B2.4-D — fetch cross-session peek aggregates for platform_dev.

import { useCallback, useEffect, useState } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { PEEK_TELEMETRY_WINDOW_DAYS } from "@/lib/activityDock/peekTelemetryTypes";

export type CrossSessionPeekStatsDto = {
  windowDays: number;
  total: number;
  uniqueUsers: number;
  uniqueOrgs: number;
  firstTs: number | null;
  lastTs: number | null;
  topReasons: ReadonlyArray<{
    reasonSectionId: string;
    count: number;
    topBucket: string | null;
    topMode: string | null;
  }>;
  perMode: Record<string, number>;
  perAssistLevel: Record<string, number>;
  perBucket: Record<string, number>;
  perReasonMode: Record<string, Record<string, number>>;
};

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; summary: CrossSessionPeekStatsDto; degraded: boolean }
  | { status: "error"; error: string }
  | { status: "hidden" };

const EMPTY_SUMMARY: CrossSessionPeekStatsDto = {
  windowDays: PEEK_TELEMETRY_WINDOW_DAYS,
  total: 0,
  uniqueUsers: 0,
  uniqueOrgs: 0,
  firstTs: null,
  lastTs: null,
  topReasons: [],
  perMode: {},
  perAssistLevel: {},
  perBucket: {},
  perReasonMode: {},
};

export function useActivityDockPeekCrossSessionStats(enabled: boolean): {
  state: State;
  reload: () => void;
} {
  const auth = useAuthOptional();
  const accessToken = (auth?.session as { access_token?: string } | null)?.access_token ?? null;
  const isPlatformDev =
    auth?.status === "ready" && auth.realHasRole("platform_dev");

  const [state, setState] = useState<State>({ status: "idle" });

  const reload = useCallback(() => {
    if (!enabled || !isPlatformDev || !accessToken) {
      setState({ status: "hidden" });
      return;
    }
    setState({ status: "loading" });
    void (async () => {
      try {
        const res = await fetch(
          `/api/activity-dock/peek-stats?windowDays=${PEEK_TELEMETRY_WINDOW_DAYS}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        );
        const json = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          error?: string;
          summary?: CrossSessionPeekStatsDto;
          degraded?: boolean;
        };
        if (!res.ok || json.ok === false) {
          setState({ status: "error", error: json.error || `HTTP ${res.status}` });
          return;
        }
        setState({
          status: "ready",
          summary: json.summary ?? EMPTY_SUMMARY,
          degraded: Boolean(json.degraded),
        });
      } catch (e: unknown) {
        setState({ status: "error", error: String((e as Error)?.message || e) });
      }
    })();
  }, [accessToken, enabled, isPlatformDev]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { state, reload };
}
